import type { PostgrestError } from '@supabase/supabase-js';
import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { WEEKDAY_SLOT_DEFINITIONS } from '@/constants/weekdaySlots';
import type { WeekdayCode } from '@/types';
import {
  ReservationAreaSlotsConfig,
  SlotsByDay,
  OldSlotsByDay,
  createEmptyOldSlotsMap,
  createEmptySlotsByDay,
} from '@/utils/commonAreaSlots';
import { toRecordArray } from '@/utils/typeGuards';

export interface ReservationArea {
  id: string;
  name: string;
  description?: string | null;
  max_reservations_per_day?: number | null;
  max_future_days?: number | null;
  abs_limit?: boolean | null;
  unique_house?: boolean | null;
  blocked_days: string[] | null;
  created_at?: string;
  reservation_payment?: boolean | null;
  only_renter?: boolean | null;
  lights?: boolean | null;
  ssid?: string | null;
  slots_config?: ReservationAreaSlotsConfig | null;
}

type SlotsColumnName = `${WeekdayCode}_slots`;
type OldSlotsColumnName = `${WeekdayCode}_old_slots`;

type ReservationAreaSlotsRow = {
  id?: string;
  area_id?: string | null;
  apply_slots?: string | null;
  updated_at?: string | null;
} & Partial<Record<SlotsColumnName, string[] | null>> &
  Partial<Record<OldSlotsColumnName, string[] | null>>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toReservationAreaSlotsRow = (value: unknown): ReservationAreaSlotsRow | null => {
  if (!isRecord(value)) {
    return null;
  }

  const row: ReservationAreaSlotsRow = {
    id: typeof value.id === 'string' ? value.id : undefined,
    area_id: typeof value.area_id === 'string' ? value.area_id : null,
    apply_slots: typeof value.apply_slots === 'string' ? value.apply_slots : null,
    updated_at: typeof value.updated_at === 'string' ? value.updated_at : null,
  };

  WEEKDAY_SLOT_DEFINITIONS.forEach((def) => {
    const slots = value[def.slotsColumn];
    row[def.slotsColumn] = Array.isArray(slots)
      ? slots.filter((item): item is string => typeof item === 'string')
      : null;

    const oldSlots = value[def.oldSlotsColumn];
    row[def.oldSlotsColumn] = Array.isArray(oldSlots)
      ? oldSlots.filter((item): item is string => typeof item === 'string')
      : null;
  });

  return row;
};

const toReservationArea = (value: unknown): ReservationArea | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null;
  }

  return {
    id: value.id,
    name: value.name,
    description: typeof value.description === 'string' ? value.description : null,
    max_reservations_per_day:
      typeof value.max_reservations_per_day === 'number' ? value.max_reservations_per_day : null,
    max_future_days:
      typeof value.max_future_days === 'number' ? value.max_future_days : null,
    abs_limit: typeof value.abs_limit === 'boolean' ? value.abs_limit : null,
    unique_house: typeof value.unique_house === 'boolean' ? value.unique_house : null,
    blocked_days: Array.isArray(value.blocked_days)
      ? value.blocked_days.filter((item): item is string => typeof item === 'string')
      : [],
    created_at: typeof value.created_at === 'string' ? value.created_at : undefined,
    reservation_payment:
      typeof value.reservation_payment === 'boolean' ? value.reservation_payment : null,
    only_renter: typeof value.only_renter === 'boolean' ? value.only_renter : null,
    lights: typeof value.lights === 'boolean' ? value.lights : null,
    ssid: typeof value.ssid === 'string' ? value.ssid : null,
    slots_config: null,
  };
};

const extractSlotsRow = (
  relation?: ReservationAreaSlotsRow | ReservationAreaSlotsRow[] | null,
): ReservationAreaSlotsRow | null => {
  if (!relation) {
    return null;
  }
  if (Array.isArray(relation)) {
    return relation[0] ?? null;
  }
  return relation;
};

const mapSlotsRowToConfig = (
  relation?: ReservationAreaSlotsRow | ReservationAreaSlotsRow[] | null,
): ReservationAreaSlotsConfig | null => {
  const row = extractSlotsRow(relation);
  if (!row) {
    return null;
  }

  const slotsByDay = createEmptySlotsByDay();
  const oldSlotsByDay = createEmptyOldSlotsMap();

  WEEKDAY_SLOT_DEFINITIONS.forEach(def => {
    const current = row[def.slotsColumn];
    slotsByDay[def.code] = Array.isArray(current) ? [...current] : [];

    const currentOld = row[def.oldSlotsColumn];
    oldSlotsByDay[def.code] = Array.isArray(currentOld) && currentOld.length > 0 ? [...currentOld] : null;
  });

  return {
    slotsByDay,
    oldSlotsByDay,
    applySlots: row.apply_slots ?? null,
  };
};

const mapSlotsRowToOldSlots = (
  relation?: ReservationAreaSlotsRow | ReservationAreaSlotsRow[] | null,
): OldSlotsByDay => {
  const oldSlotsByDay = createEmptyOldSlotsMap();
  const row = extractSlotsRow(relation);
  if (!row) {
    return oldSlotsByDay;
  }

  WEEKDAY_SLOT_DEFINITIONS.forEach(def => {
    const currentOld = row[def.oldSlotsColumn];
    oldSlotsByDay[def.code] = Array.isArray(currentOld) && currentOld.length > 0 ? [...currentOld] : null;
  });

  return oldSlotsByDay;
};

const isApplyDateInFuture = (applyDate?: string | null) => {
  if (!applyDate) {
    return false;
  }

  const targetDate = new Date(`${applyDate}T00:00:00Z`);
  if (Number.isNaN(targetDate.getTime())) {
    return false;
  }

  return targetDate.getTime() > Date.now();
};

const buildSlotsPayload = (
  areaId: string,
  slotsByDay: SlotsByDay,
  oldSlotsByDay: OldSlotsByDay,
  slotsApplyDate: string | null,
): ReservationAreaSlotsRow & { area_id: string } => {
  const payload: ReservationAreaSlotsRow & { area_id: string } = {
    area_id: areaId,
    apply_slots: slotsApplyDate ?? null,
  };

  WEEKDAY_SLOT_DEFINITIONS.forEach(def => {
    payload[def.slotsColumn] = Array.isArray(slotsByDay[def.code]) ? [...slotsByDay[def.code]] : [];
    const oldValues = oldSlotsByDay[def.code];
    payload[def.oldSlotsColumn] = Array.isArray(oldValues) && oldValues.length > 0 ? [...oldValues] : null;
  });

  return payload;
};

const saveReservationAreaSlots = async (
  areaId: string,
  slotsByDay: SlotsByDay,
  oldSlotsByDay: OldSlotsByDay,
  slotsApplyDate: string | null,
) => {
  const existingResult = await supabase
    .from('reservation_areas_slots')
    .select(SLOT_SELECTION_COLUMNS)
    .eq('area_id', areaId)
    .maybeSingle();

  if (existingResult.error && existingResult.error.code !== 'PGRST116') {
    throw existingResult.error;
  }

  const existingSlotsRow = toReservationAreaSlotsRow(existingResult.data);

  const oldSlotsToPersist = isApplyDateInFuture(existingSlotsRow?.apply_slots)
    ? mapSlotsRowToOldSlots(existingSlotsRow)
    : oldSlotsByDay;

  const slotsPayload = buildSlotsPayload(areaId, slotsByDay, oldSlotsToPersist, slotsApplyDate ?? null);

  if (existingSlotsRow?.id) {
    const updateResult = await supabase
      .from('reservation_areas_slots')
      .update(slotsPayload)
      .eq('id', existingSlotsRow.id)
      .select()
      .single();

    if (updateResult.error) throw updateResult.error;
    return toReservationAreaSlotsRow(updateResult.data);
  }

  const insertResult = await supabase
    .from('reservation_areas_slots')
    .insert([slotsPayload])
    .select()
    .single();

  if (insertResult.error) throw insertResult.error;
  return toReservationAreaSlotsRow(insertResult.data);
};

const SLOT_SELECTION_COLUMNS = [
  'id',
  'area_id',
  'apply_slots',
  ...WEEKDAY_SLOT_DEFINITIONS.flatMap(def => [def.slotsColumn, def.oldSlotsColumn]),
].join(', ');

const fetchSlotsRowsForAreaIds = async (
  areaIds: string[],
): Promise<Map<string, ReservationAreaSlotsRow>> => {
  const uniqueIds = Array.from(new Set(areaIds.filter(id => typeof id === 'string' && id.trim().length > 0)));
  if (uniqueIds.length === 0) {
    return new Map();
  }

  const { data, error } = await supabase
    .from('reservation_areas_slots')
    .select(SLOT_SELECTION_COLUMNS)
    .in('area_id', uniqueIds);

  if (error) throw error;

  const map = new Map<string, ReservationAreaSlotsRow>();
  const rows = toRecordArray(data)
    .map(toReservationAreaSlotsRow)
    .filter((row): row is ReservationAreaSlotsRow => Boolean(row));
  rows.forEach(row => {
    if (!row?.area_id) {
      return;
    }
    map.set(row.area_id, row);
  });

  return map;
};

interface ReservationAreaRow {
  community_id: string | null;
  reservation_areas: ReservationArea | ReservationArea[] | null;
}

export interface ReservationAreaWithCommunities extends ReservationArea {
  community_ids: string[];
}

const extractReservationArea = (
  relation: ReservationAreaRow['reservation_areas'],
): ReservationArea | null => {
  if (Array.isArray(relation)) {
    return relation[0] ?? null;
  }
  return relation ?? null;
};

const toReservationAreaRow = (value: unknown): ReservationAreaRow | null => {
  if (!isRecord(value)) {
    return null;
  }

  const reservationAreasValue = value.reservation_areas;
  const reservationAreas = Array.isArray(reservationAreasValue)
    ? reservationAreasValue
        .map(toReservationArea)
        .filter((area): area is ReservationArea => Boolean(area))
    : toReservationArea(reservationAreasValue);

  return {
    community_id: typeof value.community_id === 'string' ? value.community_id : null,
    reservation_areas: reservationAreas,
  };
};

export async function getReservationAreasByCommunity(communityId: string): Promise<ReservationAreaWithCommunities[]> {
  await ensureAuthenticatedSession();
  const { data, error } = await supabase
    .from('community_zone')
    .select(
      `community_id, zone_id, reservation_areas(id, name, description, max_reservations_per_day, max_future_days, abs_limit, unique_house, blocked_days, reservation_payment, only_renter, lights, ssid, created_at)`,
    )
    .eq('community_id', communityId);

  if (error) throw error;

  const rows = toRecordArray(data)
    .map(toReservationAreaRow)
    .filter((row): row is ReservationAreaRow => Boolean(row));
  const slotRowsMap = await fetchSlotsRowsForAreaIds(
    rows
      .map(row => extractReservationArea(row?.reservation_areas)?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const areasMap = new Map<string, { area: ReservationArea; communityIds: Set<string> }>();

  rows.forEach((row: ReservationAreaRow) => {
    const area = extractReservationArea(row?.reservation_areas);
    if (!area?.id) return;

      const slotsConfig = mapSlotsRowToConfig(slotRowsMap.get(area.id));
      const normalizedArea: ReservationArea = {
        ...area,
        only_renter: area.only_renter ?? false,
        unique_house: area.unique_house ?? false,
        blocked_days: area.blocked_days ?? [],
        lights: area.lights ?? false,
        ssid: area.ssid ?? null,
        slots_config: slotsConfig,
      };

    const current = areasMap.get(area.id);
    if (current) {
      if (row?.community_id) {
        current.communityIds.add(row.community_id);
      }
      return;
    }

    areasMap.set(area.id, {
      area: normalizedArea,
      communityIds: new Set(row?.community_id ? [row.community_id] : []),
    });
  });

  return Array.from(areasMap.values()).map(({ area, communityIds }) => ({
    ...area,
    community_ids: Array.from(communityIds),
  }));
}

interface ReservationAreaWithCommunityLink {
  community_id: string | null;
}

interface ReservationAreaWithCommunityRow extends ReservationArea {
  community_zone: ReservationAreaWithCommunityLink[] | null;
}

const toReservationAreaWithCommunityLink = (
  value: unknown,
): ReservationAreaWithCommunityLink | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    community_id: typeof value.community_id === 'string' ? value.community_id : null,
  };
};

const toReservationAreaWithCommunityRow = (
  value: unknown,
): ReservationAreaWithCommunityRow | null => {
  const area = toReservationArea(value);
  if (!area || !isRecord(value)) {
    return null;
  }

  const communityZoneValue = value.community_zone;
  const communityZone = Array.isArray(communityZoneValue)
    ? communityZoneValue
        .map(toReservationAreaWithCommunityLink)
        .filter((link): link is ReservationAreaWithCommunityLink => Boolean(link))
    : null;

  return {
    ...area,
    community_zone: communityZone,
  };
};

export async function getReservationAreasWithCommunities(): Promise<ReservationAreaWithCommunityRow[]> {
  await ensureAuthenticatedSession();
  const { data, error } = await supabase
    .from('reservation_areas')
    .select(
      `id, name, description, max_reservations_per_day, max_future_days, abs_limit, unique_house, blocked_days, reservation_payment, only_renter, lights, ssid, created_at, community_zone!left(community_id)`,
    );

  if (error) throw error;

  const rows = toRecordArray(data)
    .map(toReservationAreaWithCommunityRow)
    .filter((row): row is ReservationAreaWithCommunityRow => Boolean(row));
  const slotRowsMap = await fetchSlotsRowsForAreaIds(rows.map(row => row.id).filter((id): id is string => Boolean(id)));

  return rows.map(row => ({
    ...row,
    only_renter: row?.only_renter ?? false,
    unique_house: row?.unique_house ?? false,
    blocked_days: row.blocked_days ?? [],
    lights: row.lights ?? false,
    ssid: row.ssid ?? null,
    slots_config: mapSlotsRowToConfig(row?.id ? slotRowsMap.get(row.id) : null),
  }));
}

export async function getReservationAreasCount(): Promise<number> {
  await ensureAuthenticatedSession();
  const { count, error } = await supabase
    .from('reservation_areas')
    .select('id', { count: 'exact', head: true });

  if (error) throw error;

  return count ?? 0;
}

interface CreateReservationAreaInput {
  communityId?: string;
  communityIds?: string[];
  name: string;
  description?: string | null;
  max_reservations_per_day?: number | null;
  max_future_days?: number | null;
  abs_limit?: boolean | null;
  unique_house?: boolean | null;
  blocked_days?: string[];
  reservation_payment?: boolean | null;
  only_renter?: boolean | null;
  lights?: boolean | null;
  ssid?: string | null;
  slotsByDay: SlotsByDay;
  oldSlotsByDay?: OldSlotsByDay;
  slotsApplyDate?: string | null;
}

type CreateReservationAreaResponse = {
  success?: boolean;
  area_id?: string;
  area?: (ReservationArea & { community_ids?: string[] | null }) | null;
  error?: string;
  details?: string;
};

const toCreateReservationAreaResponse = (
  value: unknown,
): CreateReservationAreaResponse | null => {
  if (!isRecord(value)) {
    return null;
  }

  const areaValue = value.area;
  const area = isRecord(areaValue)
    ? (() => {
        const parsedArea = toReservationArea(areaValue);
        if (!parsedArea) {
          return null;
        }

        const rawSlotsConfig = isRecord(areaValue.slots_config)
          ? areaValue.slots_config
          : null;
        const slotsByDay = createEmptySlotsByDay();
        const oldSlotsByDay = createEmptyOldSlotsMap();

        if (rawSlotsConfig) {
          WEEKDAY_SLOT_DEFINITIONS.forEach((def) => {
            const currentSlots = rawSlotsConfig.slotsByDay;
            const currentOldSlots = rawSlotsConfig.oldSlotsByDay;

            if (isRecord(currentSlots)) {
              const daySlots = currentSlots[def.code];
              if (Array.isArray(daySlots)) {
                slotsByDay[def.code] = daySlots.filter(
                  (item): item is string => typeof item === 'string'
                );
              }
            }

            if (isRecord(currentOldSlots)) {
              const dayOldSlots = currentOldSlots[def.code];
              if (Array.isArray(dayOldSlots)) {
                oldSlotsByDay[def.code] = dayOldSlots.filter(
                  (item): item is string => typeof item === 'string'
                );
              }
            }
          });
        }

        return {
          ...parsedArea,
          community_ids: Array.isArray(areaValue.community_ids)
            ? areaValue.community_ids.filter((item): item is string => typeof item === 'string')
            : null,
          slots_config: rawSlotsConfig
            ? {
                slotsByDay,
                oldSlotsByDay,
                applySlots:
                  typeof rawSlotsConfig.applySlots === 'string'
                    ? rawSlotsConfig.applySlots
                    : null,
              }
            : null,
        };
      })()
    : null;

  return {
    success: typeof value.success === 'boolean' ? value.success : undefined,
    area_id: typeof value.area_id === 'string' ? value.area_id : undefined,
    area,
    error: typeof value.error === 'string' ? value.error : undefined,
    details: typeof value.details === 'string' ? value.details : undefined,
  };
};

export async function createReservationArea({
  communityId,
  communityIds,
  name,
  description = null,
  max_reservations_per_day = null,
  max_future_days = null,
  abs_limit = null,
  unique_house = null,
  blocked_days = [],
  reservation_payment = null,
  only_renter = null,
  lights = null,
  ssid = null,
  slotsByDay,
  oldSlotsByDay,
  slotsApplyDate = null,
}: CreateReservationAreaInput) {
  const session = await ensureAuthenticatedSession();
  const target = `${SUPABASE_URL}/functions/v1/create-reservation-area`;

  const response = await fetch(target, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      community_id: communityId,
      community_ids: communityIds,
      name,
      description,
      max_reservations_per_day,
      max_future_days,
      abs_limit,
      unique_house,
      blocked_days: blocked_days ?? [],
      reservation_payment,
      only_renter,
      lights,
      ssid,
      slots_by_day: slotsByDay,
      old_slots_by_day: oldSlotsByDay ?? createEmptyOldSlotsMap(),
      slots_apply_date: slotsApplyDate,
    }),
  });

  if (!response.ok) {
    let details = 'No se pudo crear el área común.';
    try {
      const parsed = toCreateReservationAreaResponse(await response.json() as unknown);
      details = parsed?.error || parsed?.details || details;
    } catch (parseError) {
      console.error('No se pudo parsear la respuesta de la función', parseError);
    }
    throw new Error(details);
  }

  const data = toCreateReservationAreaResponse(await response.json() as unknown);

  if (!data?.success || typeof data.area_id !== 'string') {
    const details = data?.error ?? data?.details ?? 'No se pudo crear el área común.';
    throw new Error(details);
  }

  if (data.area) {
    const slotsConfig = data.area.slots_config
      ? {
          ...data.area.slots_config,
          slotsByDay: data.area.slots_config.slotsByDay ?? createEmptySlotsByDay(),
          oldSlotsByDay: data.area.slots_config.oldSlotsByDay ?? createEmptyOldSlotsMap(),
          applySlots: data.area.slots_config.applySlots ?? null,
        }
      : null;

    return {
      ...data.area,
      blocked_days: data.area.blocked_days ?? [],
      reservation_payment: data.area.reservation_payment ?? null,
      only_renter: data.area.only_renter ?? null,
      unique_house: data.area.unique_house ?? null,
      lights: data.area.lights ?? false,
      ssid: data.area.ssid ?? null,
      slots_config: slotsConfig,
      community_ids: data.area.community_ids ?? [],
    } satisfies ReservationArea & { community_ids?: string[] };
  }

  try {
    const createdArea = await getReservationArea(data.area_id);
    if (createdArea) {
      return createdArea;
    }
  } catch (fetchError) {
    console.error('No se pudo recuperar el área recién creada', fetchError);
  }

  return {
    id: data.area_id,
    name,
    description,
    max_reservations_per_day,
    max_future_days,
    abs_limit,
    unique_house,
    blocked_days: blocked_days ?? [],
    reservation_payment,
    only_renter,
    lights: lights ?? false,
    ssid,
    slots_config: {
      slotsByDay,
      oldSlotsByDay: oldSlotsByDay ?? createEmptyOldSlotsMap(),
      applySlots: slotsApplyDate ?? null,
    },
  };
}

export async function getReservationArea(area_id: string): Promise<ReservationArea | null> {
  await ensureAuthenticatedSession();
  const { data, error } = await supabase
    .from('reservation_areas')
    .select(
      `id, name, description, max_reservations_per_day, max_future_days, abs_limit, unique_house, blocked_days, reservation_payment, only_renter, lights, ssid, created_at`,
    )
    .eq('id', area_id)
    .single();
  if (error) {
    const typedError = error as PostgrestError;
    // Si no existe devolvemos null, otros errores se relanzan
    if (typedError.code === 'PGRST116') return null; // not found
    throw error;
  }

  const area = toReservationArea(data);

  if (!area?.id) {
    return area;
  }

  const slotRowsMap = await fetchSlotsRowsForAreaIds([area.id]);
  const slotRow = slotRowsMap.get(area.id) ?? null;

  return {
    ...area,
    only_renter: area.only_renter ?? false,
    unique_house: area.unique_house ?? false,
    blocked_days: area.blocked_days ?? [],
    lights: area.lights ?? false,
    ssid: area.ssid ?? null,
    slots_config: mapSlotsRowToConfig(slotRow),
  };
}

export async function updateReservationAreaLimits(
  area_id: string,
  limits: Partial<{ max_reservations_per_day: number | null; max_future_days: number | null }>,
) {
  await ensureAuthenticatedSession();
  const payload: Partial<{ max_reservations_per_day: number | null; max_future_days: number | null }> = {};

  if (limits.max_reservations_per_day !== undefined) {
    payload.max_reservations_per_day = limits.max_reservations_per_day;
  }

  if (limits.max_future_days !== undefined) {
    payload.max_future_days = limits.max_future_days;
  }

  if (Object.keys(payload).length === 0) {
    throw new Error('Debe especificarse al menos un límite para actualizar.');
  }

  const result = await supabase
    .from('reservation_areas')
    .update(payload)
    .eq('id', area_id)
    .select()
    .single();
  if (result.error) throw result.error;
  return toReservationArea(result.data);
}

export async function updateReservationAreaMaxPerDay(area_id: string, max: number) {
  return updateReservationAreaLimits(area_id, { max_reservations_per_day: max });
}

export async function getReservationAreasByIds(areaIds: string[]): Promise<ReservationArea[]> {
  await ensureAuthenticatedSession();
  const uniqueIds = Array.from(new Set(areaIds.map(id => id.trim()).filter(id => id.length > 0)));

  if (uniqueIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('reservation_areas')
    .select(
      `id, name, description, max_reservations_per_day, max_future_days, abs_limit, unique_house, blocked_days, reservation_payment, only_renter, lights, ssid, created_at`,
    )
    .in('id', uniqueIds);

  if (error) throw error;
  const rows = toRecordArray(data)
    .map(toReservationArea)
    .filter((row): row is ReservationArea => Boolean(row));
  const slotRowsMap = await fetchSlotsRowsForAreaIds(rows.map(row => row.id).filter((id): id is string => Boolean(id)));

  return rows.map(row => ({
    ...row,
    only_renter: row.only_renter ?? false,
    unique_house: row.unique_house ?? false,
    blocked_days: row.blocked_days ?? [],
    lights: row.lights ?? false,
    ssid: row.ssid ?? null,
    slots_config: mapSlotsRowToConfig(row?.id ? slotRowsMap.get(row.id) : null),
  }));
}

type ReservationAreaUpdatePayload = Partial<{
  name: string;
  description: string | null;
  max_reservations_per_day: number;
  max_future_days: number | null;
  abs_limit: boolean | null;
  unique_house: boolean | null;
  blocked_days: string[];
  reservation_payment: boolean | null;
  only_renter: boolean | null;
  lights: boolean | null;
  ssid: string | null;
}> & {
  slotsByDay?: SlotsByDay;
  oldSlotsByDay?: OldSlotsByDay;
  slotsApplyDate?: string | null;
};

export async function updateReservationArea(area_id: string, updates: ReservationAreaUpdatePayload) {
  await ensureAuthenticatedSession();

  const {
    slotsByDay,
    oldSlotsByDay,
    slotsApplyDate,
    ...baseUpdates
  } = updates;

  const payload: Partial<ReservationArea> = {};

  if (baseUpdates.name !== undefined) payload.name = baseUpdates.name;
  if (baseUpdates.description !== undefined) payload.description = baseUpdates.description;
  if (baseUpdates.max_reservations_per_day !== undefined) {
    payload.max_reservations_per_day = baseUpdates.max_reservations_per_day;
  }
  if (baseUpdates.max_future_days !== undefined) {
    payload.max_future_days = baseUpdates.max_future_days;
  }
  if (baseUpdates.abs_limit !== undefined) payload.abs_limit = baseUpdates.abs_limit;
  if (baseUpdates.unique_house !== undefined) payload.unique_house = baseUpdates.unique_house;
  if (baseUpdates.blocked_days !== undefined) payload.blocked_days = baseUpdates.blocked_days;
  if (baseUpdates.reservation_payment !== undefined) {
    payload.reservation_payment = baseUpdates.reservation_payment;
  }
  if (baseUpdates.only_renter !== undefined) payload.only_renter = baseUpdates.only_renter;
  if (baseUpdates.lights !== undefined) payload.lights = baseUpdates.lights;
  if (baseUpdates.ssid !== undefined) payload.ssid = baseUpdates.ssid;

  if (Object.keys(payload).length > 0) {
    const { error: updateError } = await supabase
      .from('reservation_areas')
      .update(payload)
      .eq('id', area_id);
    if (updateError) throw updateError;
  }

  if (slotsByDay) {
    await saveReservationAreaSlots(
      area_id,
      slotsByDay,
      oldSlotsByDay ?? createEmptyOldSlotsMap(),
      slotsApplyDate ?? null,
    );
  }

  return getReservationArea(area_id);
}

export async function setReservationAreaCommunities(area_id: string, communityIds: string[]) {
  await ensureAuthenticatedSession();
  const trimmedIds = Array.from(
    new Set(communityIds.map(id => id.trim()).filter(id => id.length > 0)),
  );

  const existingLinksResult = await supabase
    .from('community_zone')
    .select('community_id, zone_id')
    .eq('zone_id', area_id);

  if (existingLinksResult.error) throw existingLinksResult.error;

  const existingLinks = toRecordArray(existingLinksResult.data)
    .map((link) => ({
      community_id: typeof link.community_id === 'string' ? link.community_id : null,
      zone_id: typeof link.zone_id === 'string' ? link.zone_id : null,
    }))
    .filter((link): link is { community_id: string; zone_id: string | null } => link.community_id !== null);

  const existingIds = new Set(existingLinks.map(link => link.community_id));
  const desiredIds = new Set(trimmedIds);

  const toInsert = trimmedIds.filter(id => !existingIds.has(id));
  const toRemove = existingLinks
    .filter(link => !desiredIds.has(link.community_id))
    .map(link => link.community_id);

  if (toInsert.length > 0) {
    const { error: insertError } = await supabase
      .from('community_zone')
      .insert(toInsert.map(community_id => ({ community_id, zone_id: area_id })));
    if (insertError) throw insertError;
  }

  if (toRemove.length > 0) {
    const { error: deleteError } = await supabase
      .from('community_zone')
      .delete()
      .eq('zone_id', area_id)
      .in('community_id', toRemove);
    if (deleteError) throw deleteError;
  }

  return trimmedIds;
}

export async function deleteReservationArea(area_id: string) {
  await ensureAuthenticatedSession();
  const { error } = await supabase
    .from('reservation_areas')
    .delete()
    .eq('id', area_id);

  if (error) throw error;
}

export async function removeReservationAreaFromCommunity(area_id: string, community_id: string) {
  await ensureAuthenticatedSession();
  const { error } = await supabase
    .from('community_zone')
    .delete()
    .eq('zone_id', area_id)
    .eq('community_id', community_id);

  if (error) throw error;
}
